import { cancelCall } from 'common/helpers/api';
import { showNotification, hideNotification } from 'jitsi/notifications/actions';
import { UPDATE_CALL_STATUS, UPDATE_CALLEE, SET_CALL_NOTIFICATION_UID, UPDATE_ORGANIZATIONS, UPDATE_IS_LISTENER_ADDED, } from './actionTypes';
import { BUTTON_TYPES } from 'jitsi/base/ui/constants.any';
import { NOTIFICATION_TIMEOUT_TYPE } from 'jitsi/notifications/constants';
import { batch } from 'react-redux';
/**
 * Action to update the invite callee.
 *
 * @param {string} status - The new call status.
 * @param {Object} calleeParam - The callee.
 * @returns {{
 *     type: UPDATE_CALL_STATUS,
 *     status: string
 * }}
 */
export function updateCallStatus(status, calleeParam = null) {
    return async (dispatch) => {
        const callee = APP.store.getState()['features/call'].callee || calleeParam;
        switch (status) {
            case 'preparing':
                dispatch(showCallPreparingNotification(callee.name));
                break;
            case 'ringing':
                dispatch(showCallRingingNotification(callee.name));
                break;
            case 'accepted':
                dispatch(showCallAcceptedNotification(callee.name));
                break;
            case 'rejected':
                dispatch(showCallRejectedNotification(callee.name));
                break;
            case 'timedout':
                dispatch(showCallTimedOutNotification(callee.name));
                break;
            case 'error':
                dispatch(showCallErrorNotification());
                break;
            case 'requested':
                dispatch(showCallRequestNotification(callee.name));
                break;
            default:
                break;
        }
        dispatch({
            type: UPDATE_CALL_STATUS,
            status,
        });
    };
}
/**
 * Action to update invite callee.
 *
 * @param {Object} callee - The new callee object.
 * @returns {{
 *     type: UPDATE_CALLEE,
 *     callee: Object
 * }}
 */
export function updateCallee(callee) {
    return {
        type: UPDATE_CALLEE,
        callee,
    };
}
/**
 * Action to update callable users.
 *
 * @param {Object} users - User list for invitation.
 * @returns {{
 *     type: UPDATE_ORGANIZATIONS,
 *     callee: Object
 * }}
 */
export function updateCallableUsers(organizations) {
    return {
        type: UPDATE_ORGANIZATIONS,
        organizations,
    };
}
/**
 * Action to update invite callee.
 *
 * @param {Object} isListenerAdded - Is listener added.
 * @returns {{
 *     type: UPDATE_IS_LISTENER_ADDED,
 *     status: boolean
 * }}
 */
export function updateIsListenerAdded(isListenerAdded) {
    return {
        type: UPDATE_IS_LISTENER_ADDED,
        isListenerAdded,
    };
}
/**
 * Sets UID of the the pending CALL notification to use it when hinding
 * the notification is necessary, or unsets it when undefined (or no param) is
 * passed.
 *
 * @param {?number} uid - The UID of the notification.
 * @returns {{
 *     type: SET_CALL_NOTIFICATION_UID,
 *     uid: number
 * }}
 */
function _setCallNotificationUid(uid) {
    return {
        type: SET_CALL_NOTIFICATION_UID,
        uid,
    };
}
const _getCustomButtonClickHandler = (dispatch, state) => async () => {
    const token = localStorage.getItem('11S_TOKEN');
    const { callId, temporaryToken } = state['features/call'].callee || {};
    if (callId && temporaryToken)
        await cancelCall(token, callId, temporaryToken);
    batch(() => {
        dispatch(updateCallee(null));
        dispatch(updateCallStatus(null));
        dispatch(showCallCancelledNotification());
    });
    return true;
};
export function showCallPreparingNotification(calleeName) {
    return async (dispatch, getState) => {
        const notification = await dispatch(showNotification({
            descriptionKey: 'callUser.preparingText',
            descriptionArguments: { calleeName },
            titleKey: 'callUser.notificationTitle',
            customActionType: [BUTTON_TYPES.DESTRUCTIVE],
            customActionNameKey: ['callUser.cancelButtonText'],
            customActionHandler: [_getCustomButtonClickHandler(dispatch, getState())],
        }));
        if (notification)
            dispatch(_setCallNotificationUid(notification.uid));
    };
}
/**
 * Signals that the ringing call notification should be shown on the
 * screen.
 *
 * @param {?number} calleeName - The calleeName to display in notification.
 * @returns {Function}
 */
export function showCallRingingNotification(calleeName) {
    return async (dispatch, getState) => {
        const state = getState();
        const { uid } = state['features/call'];
        if (uid)
            await dispatch(hideNotification(uid));
        const notification = await dispatch(showNotification({
            descriptionKey: 'callUser.ringingText',
            descriptionArguments: { calleeName },
            titleKey: 'callUser.notificationTitle',
            customActionType: [BUTTON_TYPES.DESTRUCTIVE],
            customActionNameKey: ['callUser.cancelButtonText'],
            customActionHandler: [_getCustomButtonClickHandler(dispatch, state)],
        }));
        if (notification)
            dispatch(_setCallNotificationUid(notification.uid));
    };
}
const _hideCallNotification = (notificationDetails) => {
    return async (dispatch, getState) => {
        const { uid } = getState()['features/call'];
        batch(() => {
            if (uid)
                dispatch(hideNotification(uid));
            dispatch(showNotification(notificationDetails, NOTIFICATION_TIMEOUT_TYPE.SHORT));
            dispatch(updateCallee(null));
            dispatch(updateCallStatus(null));
        });
    };
};
/**
 * Signals that the rejected call notification should be shown on the
 * screen.
 *
 * @param {string} calleeName - The calleeName to display in notification.
 * @returns {Function}
 */
export function showCallRejectedNotification(calleeName) {
    return _hideCallNotification({
        titleKey: 'callUser.notificationTitle',
        descriptionKey: 'callUser.rejectedText',
        descriptionArguments: { calleeName },
    });
}
/**
 * Signals that the cancelled call notification should be shown on the
 * screen.
 *
 * @returns {Function}
 */
export function showCallCancelledNotification() {
    return _hideCallNotification({
        titleKey: 'callUser.notificationTitle',
        descriptionKey: 'callUser.cancelledText',
    });
}
/**
 * Signals that the error notification should be shown on the
 * screen.
 *
 * @returns {Function}
 */
export function showCallErrorNotification() {
    return _hideCallNotification({
        titleKey: 'callUser.notificationTitle',
        descriptionKey: 'callUser.errorText',
    });
}
/**
 * Signals that the timedout call notification should be shown on the
 * screen.
 *
 * @param {string} calleeName - The calleeName to display in notification.
 * @returns {Function}
 */
export function showCallTimedOutNotification(calleeName) {
    return _hideCallNotification({
        titleKey: 'callUser.notificationTitle',
        descriptionKey: 'callUser.timedoutText',
        descriptionArguments: { calleeName },
    });
}
/**
 * Signals that the accepted call notification should be shown on the
 * screen.
 *
 * @param {string} calleeName - The calleeName to display in notification.
 * @returns {Function}
 */
export function showCallAcceptedNotification(calleeName) {
    return _hideCallNotification({
        titleKey: 'callUser.notificationTitle',
        descriptionKey: 'callUser.acceptedText',
        descriptionArguments: { calleeName },
    });
}
/**
 * Signals that the rejected call notification should be shown on the
 * screen.
 *
 * @param {string} calleeName - The calleeName to display in notification.
 * @returns {Function}
 */
export function showCallRequestNotification(calleeName) {
    return _hideCallNotification({
        titleKey: 'callUser.notificationTitle',
        descriptionKey: 'callUser.requestText',
        descriptionArguments: { calleeName },
    });
}
